import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { Product, ProductTag, SimpleProduct } from '@axis/graphql';
import { ProductCard } from '@axis/components/ProductCard';
import { ProductCardGridFilters } from '@axis/components/ProductCardGridFilters';
import { useAnimation } from '@axis/components/AnimationProvider';
import { useProductFilters } from '@axis/hooks/useProductFilters';
import type { SortByEnum, SortAndFilterState } from '@axis/components/ProductCardGridFilters';

// Converts sortby query param to sortBy filter
function getSortBy(slug?: string): SortByEnum {
  switch (slug) {
    case 'low_to_high':
      return 'LOW_TO_HIGH';
    case 'high_to_low':
      return 'HIGH_TO_LOW';
    case 'menu_order':
    default:
      return 'MENU_ORDER';
  }
}

// Converts tags query param to tags filter.
function getTags(tagSlugs?: string): string[] {
  const tags: string[] = [];

  if (tagSlugs) {
    tags.push(...tagSlugs.split(','));
  }

  return tags;
}

export interface ProductCardGridProps {
  products: Product[];
  tags: ProductTag[];
}

export function ProductCardGrid({ products, tags: allTags }: ProductCardGridProps) {
  const { newTimeline, add, anime } = useAnimation();
  const {
    query, push, asPath: activePath,
  } = useRouter();

  // Selected product.
  const selected = (query?.slug as string) || undefined;

  // Text search state.
  const [searchText, setSearchText] = useState<string>((query?.search as string) || '');
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  // Filter state.
  const [filtersState, updateFilters] = useState<SortAndFilterState>({
    sortBy: getSortBy(query?.sortby as string),
    tags: getTags(query?.tags as string),
  });

  // Updates browser path if filters change.
  const maybeUpdatePath = () => new Promise(() => {
    let nextPath = '/shop';
    const shallow = activePath.includes('/shop');

    if (selected) {
      nextPath = `/shop/${selected}`;
    }

    // Add search text query param to path.
    const showSearchParam = !!searchText;
    nextPath += showSearchParam ? `?search=${searchText}` : '';

    // Add sortby query param.
    const showSortByParam = filtersState.sortBy !== 'MENU_ORDER';
    if (showSortByParam) {
      nextPath += showSearchParam ? '&' : '?';
      nextPath += `sortby=${filtersState.sortBy.toLowerCase()}`;
    }

    // Add tags query param.
    const showTagsParam = filtersState.tags.length;
    if (showTagsParam) {
      nextPath
      += showSearchParam || showSortByParam ? '&' : '?';
      nextPath += `tags=${filtersState.tags.join(',')}`;
    }

    if (nextPath !== activePath) {
      push(nextPath, undefined, { shallow });
    }
  });

  // Processes search filters if any changes.
  const items = useProductFilters(
    products,
    {
      searchText: debouncedSearchText,
      tags: filtersState.tags,
      sortBy: filtersState.sortBy,
    },
    [
      products,
      debouncedSearchText,
      filtersState.tags,
      filtersState.sortBy,
    ],
  );

  // Update path if any changes.
  useEffect(() => {
    maybeUpdatePath();
  }, [
    products,
    debouncedSearchText,
    filtersState.tags,
    filtersState.sortBy,
  ]);

  // Update debouncedSearchText when searchText changes.
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  useEffect(() => {
    newTimeline(
      'product-card-grid-sections',
      {
        direction: 'normal',
        autoplay: true,
        easing: 'easeInOutSine',
      },
    );
    [...Array(4)].forEach((_, index) => {
      add(
        'product-card-grid-sections',
        {
          targets: `.shop-page-section:nth-of-type(${Number(index)})`,
          opacity: [0, 1],
          duration: 300,
          delay: anime.stagger(100),
        },
      );
    });

    anime(
      {
        targets: '.shop-page-section .form-field-input',
        opacity: [0, 1],
        duration: 300,
        delay: 400,
        easing: 'easeInOutSine',
      },
    );

    newTimeline(
      'product-card-grid-cards',
      {
        direction: 'normal',
        autoplay: true,
        easing: 'easeInOutSine',
      },
    );
    items.forEach((_, index) => {
      add(
        'product-card-grid-cards',
        {
          targets: `.product-card:nth-child(${Number(index) + 1})`,
          translateY: [300, 0],
          opacity: [0, 1],
          duration: 300,
          delay: anime.stagger(10, { start: 500 }),
        },
      );
    });
  }, []);

  return (
    <div className="w-full flex flex-row flex-wrap gap-5 px-5 lg:mx-0">
      <div className="shop-page-section w-full">
        <span className="shop-page-section-header">Search</span>
        <input
          className="form-field-input w-full"
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>
      <ProductCardGridFilters
        allTags={allTags}
        filters={filtersState}
        updateFilters={updateFilters}
      />
      <div className="shop-page-section
      w-full
      items-center
      justify-items-center
      md:flex-1
      shrink
      grid
      grid-cols-1
      sm:grid-cols-1
      md:grid-cols-2
      xl:grid-cols-3"
      >
        <span className="shop-page-section-header">Results</span>
        {items.map((product) => (
          <ProductCard key={product?.id} product={product as SimpleProduct} />
        ))}
      </div>
    </div>
  );
}
